
import { useState, FormEvent } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { trackEstimateRequest, trackFormError } from "@/components/GoogleAnalytics";


interface EstimateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EstimateDialog = ({ open, onOpenChange }: EstimateDialogProps) => {
  const { toast } = useToast();
  const [step, setStep] = useState<1 | 2>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "home",
    city: "",
    timeframe: "",
    notes: "",
  });

  const close = () => {
    setStep(1);
    setForm({
      name: "",
      email: "",
      phone: "",
      service: "home",
      city: "",
      timeframe: "",
      notes: "",
    });
    onOpenChange(false);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    
    setIsSubmitting(true);

    try {
      // Use secure edge function for submission
      const { data, error } = await supabase.functions.invoke('submit-intake-request', {
        body: {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          service: form.service,
          message: form.notes.trim() || null,
          location: form.city.trim() || null,
          preferred_date: form.timeframe || null
        }
      });

      if (error) {
        console.error('Submission error:', error);
        throw new Error(error.message || 'Failed to submit request');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to submit request');
      }

      // Track successful estimate request
      trackEstimateRequest(form.service);
      
      toast({
        title: "Estimate request received",
        description: "Thanks! We'll reach out within 1 business day.",
      });
      close();

    } catch (error: any) {
      console.error("Estimate request error:", error);
      
      trackFormError('estimate_dialog', error.message || 'submission_failed');
      
      toast({
        title: "Error",
        description: error.message || "Failed to submit request. Please try again or call us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Request a Free Estimate</DialogTitle>
          <DialogDescription>
            Quick 2-step form. We serve all of Southwest Michigan (Buchanan, Niles, St. Joseph, Benton Harbor, and more).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-6">
          {step === 1 ? (
            <section className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input 
                    id="name" 
                    value={form.name} 
                    onChange={(e) => setForm({ ...form, name: e.target.value })} 
                    required 
                    minLength={2}
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    value={form.email} 
                    onChange={(e) => setForm({ ...form, email: e.target.value })} 
                    required 
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input 
                  id="phone" 
                  type="tel"
                  value={form.phone} 
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} 
                />
              </div>
              <div className="flex items-center justify-between pt-2">
                <div className="text-sm text-muted-foreground">Step 1 of 2</div>
                <Button type="button" onClick={() => setStep(2)}>Next</Button>
              </div>
            </section>
          ) : (
            <section className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Service</Label>
                  <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="home">Home Organization</SelectItem>
                      <SelectItem value="cleaning">Cleaning & Housekeeping</SelectItem>
                      <SelectItem value="adhd">ADHD Systems</SelectItem>
                      <SelectItem value="realestate">Real‑Estate Prep</SelectItem>
                      <SelectItem value="move">Move Management</SelectItem>
                      <SelectItem value="maintenance">Maintenance Plans</SelectItem>
                      <SelectItem value="estate">Estate Management</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input 
                    id="city" 
                    value={form.city} 
                    onChange={(e) => setForm({ ...form, city: e.target.value })} 
                    placeholder="e.g., Buchanan, Niles, St. Joseph"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="timeframe">Ideal timeframe</Label>
                <Input 
                  id="timeframe" 
                  value={form.timeframe} 
                  onChange={(e) => setForm({ ...form, timeframe: e.target.value })} 
                  placeholder="e.g., within 2–4 weeks" 
                />
              </div>
              <div>
                <Label htmlFor="notes">Project notes</Label>
                <Textarea 
                  id="notes" 
                  value={form.notes} 
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} 
                  placeholder="Tell us about your space, goals, and any preferences." 
                />
              </div>
              
              
              <div className="flex items-center justify-between pt-2">
                <Button type="button" variant="outline" onClick={() => setStep(1)}>Back</Button>
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="luxury-gradient text-primary-foreground"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Request Estimate"
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">We respect your privacy and never share your information.</p>
            </section>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EstimateDialog;
